/**
 * Renders the pop on the map
 * @module infoviewer
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/infoviewer|blog}
 */

import ol = require("ol");
import $ = require("jquery");
import _ = require("underscore");
import Utils = require("../utils");
import Portal = require("portal-gis");
import View = require("./view");
import { Popup } from "ol3-popup/index";
import Symbology = require("../utils/symbology");
import Converter = require("../utils/ol3/converter");
import Command = require("../commands/base-command");

const log = Utils.log;

interface Args {
  out: {
    layers: Rhythm.GIS.FeatureQueryResultLayer[];
    point: ol.Coordinate;
    pixel: ol.Pixel;
  };
}

function getAttributes(feature: ol.Feature) {
  let attrs = feature.get("attributes");
  if (!attrs) {
    attrs = {};
    feature.set("attributes", attrs);
  }
  return attrs;
}

class Control extends View {
  static about = `
The default infoviewer popup.
    `;

  static options = Utils.defaults(
    {
      "field-labels": "comma-separated list of field labels",
      "field-list": "comma-separated list of field names",
      "show-labels": "true => show field labels",
      template: "template to use when render the feature",
      "use-svg": "false => do not use svg when rendering navigation controls"
    },
    View.options
  );

  ol3popup: Popup;

  handles = <Array<{ off: () => void }>>[];

  destroy() {
    this.handles.forEach(h => h.off());
    super.destroy();
  }

  // // HACK04: there isn't actually a view being created here!
  createView() {
    // console.log("create view for infoviewer");
    // This is the view portion of a command which responds to an event (move logic from commands/popup-tool)
    let control = this.configuration;
    let mediator = this.mediator;
    let args = <Args>this.args;
    let options = this.options;

    mediator.mapView(mapView => {
      let layers = args.out.layers.filter(l => 0 < l.features.length);
      if (!layers.length) {
        log("no layers with features");
        this.ol3popup = mapView.model.get("popup");
        this.ol3popup && this.ol3popup.hide();
        return;
      }

      mediator.mapViewMap(map => {
        // load the template for this popup, each feature may have it's own replacement template
        Utils.withTemplate(options["template"], template => {
          // (re)create the popup using code-based styling to customize it for rhythm
          {
            this.ol3popup = mapView.model.get("popup");
            this.ol3popup && this.ol3popup.hide();

            this.ol3popup = Popup.create({
              map: map,
              autoPan: true,
              autoPopup: false,
              autoPanMargin: 10,
              autoPanAnimation: {
                source: null,
                duration: 200
              },
              pointerPosition: 140,
              css: `
                            .ol-popup {
                                background-color: white;
                            }
                            `,
              xOffset: 0,
              yOffset: 52,
              asContent: feature => {
                let model = this.mediator.extend(feature).model;
                let sym = Symbology.findSymbol(this.mediator, model);
                let l = feature.get("l");
                let layerConfig = <Portal.MapLayerLeaf>Utils.extend(l).config;
                let layerOptions = layerConfig ? Utils.asDictionary(layerConfig.Options.Values) : {};
                let content = document.createElement("div");

                Utils.withTemplate(sym && sym.template, t2 => {
                  /**
                   * allow template to use symbology info (like label)
                   * and layer options (like field-list)
                   */
                  t2 = t2 || template;

                  // template result can be a promise, string or DOM element
                  $.when(
                    t2(model, {
                      symbol: sym,
                      layerOptions: layerOptions,
                      commands: () => {
                        let commandNode = document.createElement("section");
                        commandNode.classList.add("commands");
                        let ext = <
                          {
                            config: Portal.MapLayerLeaf;
                          }
                        >Utils.extend(l);
                        ext.config &&
                          ext.config.Commands.Commands.filter(c => c.type === "action").forEach(c =>
                            this.addButton(c, $(commandNode))
                          );
                        ext = Utils.extend(feature);
                        ext.config &&
                          ext.config.Commands.Commands.filter(c => c.type === "action").forEach(c =>
                            this.addButton(c, $(commandNode))
                          );
                        return commandNode;
                      }
                    })
                  ).then(v => {
                    content.appendChild(v);
                  });
                });
                return content;
              }
            });

            // destroys completely so we know exactly what we're working with
            this.ol3popup.on("hide", () => {
              this.ol3popup.destroy();
              this.ol3popup = null;
              mapView.model.set("popup", null);
              this.destroy();
            });

            let prevButton = $(".btn-prev", this.ol3popup.domNode);
            let nextButton = $(".btn-next", this.ol3popup.domNode);

            if (this.options["use-svg"] !== "false") {
              $(this.ol3popup.domNode).addClass("map-location-modal-fix");
              $(".pagination", this.ol3popup.domNode).addClass("projects-pagination");
              {
                let closer = $(".ol-popup-closer", this.ol3popup.domNode);
                closer.addClass("btn-icon close-modal");
                closer.append(
                  `<svg class="icon close"><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#close"></use></svg>`
                );
              }
              prevButton.append(
                `<svg class="icon arrow left active"><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#arrow"></use></svg>`
              );
              nextButton.append(
                `<svg class="icon arrow right active"><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#arrow"></use></svg>`
              );

              this.ol3popup.pages.on("goto", () => {
                let index = this.ol3popup.pages.activeIndex;
                $("svg", prevButton).toggleClass("active", index > 0);
                $("svg", nextButton).toggleClass("active", index < this.ol3popup.pages.count - 1);
              });
            }

            mapView.model.set("popup", this.ol3popup);
          }

          // keep track of the features
          let featureInfos = <
            Array<{
              pageIndex: number;
              feature: Rhythm.GIS.FeatureQueryResultFeature;
              filter: (joinClause, feature) => boolean;
              rendered: boolean;
              pageExt: Array<string | HTMLElement>;
            }>
          >[];

          let renderPageExt = (pageIndex?: number) => {
            if (pageIndex === undefined) {
              if (!this.ol3popup) return;
              pageIndex = this.ol3popup.pages.activeIndex;
            }

            let featureInfo = Utils.first(featureInfos, f => f.pageIndex === pageIndex);
            if (!featureInfo) return;

            let activeFeature = featureInfo.feature;

            if (0 === featureInfo.pageExt.length) {
              log("no extended data");
            } else {
              let container = $("section.body", this.ol3popup.domNode);
              if (!container.length) throw "page container not found";

              let div = $("div.ext", container);
              if (!div.length) {
                div = $("<div>").appendTo(container);
                div.addClass("ext");
              }
              div.empty();
              div.append("<hr>");

              featureInfo.pageExt.forEach(v => div.append(v));
              //featureInfo.pageExt = [];
            }
          };

          let render = _.debounce(() => {
            if (!this.ol3popup) return;
            renderPageExt();
            this.ol3popup.panIntoView();
          }, 200);

          this.ol3popup.pages.on("goto", () => {
            let pageIndex = this.ol3popup.pages.activeIndex;

            render();

            // not sure why I did this...see infoviewer-paging-as-feature-click-event and/or liferay-interpreter as possible reasons
            this.args.out.activeFeature = featureInfos[pageIndex].feature;

            // let the world know the page changed
            this.trigger({
              paging: pageIndex,
              features: featureInfos.map(f => f.feature),
              activeFeature: featureInfos[pageIndex].feature
            });
          });

          // add a page for each feature of each layer
          layers.forEach(l => {
            let layerConfig = <Portal.MapLayerLeaf>Utils.extend(l).config;
            // commands get executed with popup:goto event
            let queryCommands = layerConfig
              ? layerConfig.Commands.Commands.filter(c => c.type === "infoviewer-extension")
              : [];
            // handlers for these events get hard-coded below
            let queryEvents = layerConfig
              ? layerConfig.Events.Events.filter(c => c.type === "infoviewer-extension")
              : [];

            queryEvents.forEach(event => {
              // the event has not been created so these options have not been localized
              // do it manually
              event.Options.Values.forEach(v => (v.value = Utils.translate(v.value)));
              const options = Utils.asDictionary(event.Options.Values);
              if (!options["field-list"]) {
                console.warn("field-list required");
                return;
              }

              let h = this.mediator.on(event.name, args => {
                // merge results into viewer
                if (!this.ol3popup) return;

                let command = <Command<any>>args.in;

                let layers = <Rhythm.GIS.FeatureQueryResultLayer[]>command.findArg("layers", args).layers || [];

                let fieldList = Utils.split(options["field-list"]).filter(v => !!v);
                let showLabels = options["show-labels"] === "true";
                let fieldLabelMap = [];

                if (showLabels) {
                  let fieldLabels = options["field-labels"] ? Utils.split(options["field-labels"]) : fieldList;
                  fieldList.forEach((id, i) => (fieldLabelMap[id] = fieldLabels[i]));
                }

                // delay render as master-detail grid in case there are multiple child items
                Utils.withTemplate(options["template"], template => {
                  layers.forEach(detailLayer => {
                    detailLayer.features.forEach(feature => {
                      let values = fieldList.map(k => feature.attributes[k]);
                      log("feature values", values);
                      $.when(
                        template(feature, {
                          layerOptions: options
                        })
                      ).then(v => {
                        featureInfos[this.ol3popup.pages.activeIndex].pageExt.push(v);
                        render();
                      });
                    });
                  });
                });
              });

              this.handles.push(h);
            });

            // add a page for the feature, possibly using a different template
            l.features
              .filter(feature => !!feature.geometry)
              .forEach(feature => {
                // very unhappy about all this feature conversion and attribute hacking
                // but still not a good time to fix it
                let nativeFeature = <ol.Feature>(
                  (this.mediator.extend(feature).olFeature || Utils.extend(feature).olFeature)
                );
                if (!nativeFeature) throw "native feature not associated with query result";
                nativeFeature.set("l", l);

                // click location might be in 'image-center'
                let searchCoordinate = <ol.Coordinate>JSON.parse(getAttributes(nativeFeature)["image-center"] || "[]");
                if (!searchCoordinate.length) {
                  // but if not, try an interior point or the center
                  let geom = nativeFeature.getGeometry();
                  if (geom instanceof ol.geom.Polygon) {
                    searchCoordinate = geom.getInteriorPoint().getCoordinates();
                  } else {
                    searchCoordinate = ol.extent.getCenter(geom.getExtent());
                  }
                }
                this.ol3popup.pages.addFeature(nativeFeature, {
                  searchCoordinate: searchCoordinate
                });

                let pageIndex = this.ol3popup.pages.count - 1;

                featureInfos.push({
                  pageIndex: pageIndex,
                  feature: feature,
                  filter: (joinClause, item) =>
                    <boolean>eval(_.template(`item.attributes.${joinClause}`)(feature.attributes).replace(/=/g, "===")),
                  rendered: false,
                  pageExt: []
                });

                /**
                 * When navigating to a new feature execute all the queryCommands, which will presumably
                 * trigger queryEvents, which have handlers hard-coded above.
                 */
                this.ol3popup.pages.on("goto", () => {
                  if (pageIndex !== this.ol3popup.pages.activeIndex) return;
                  let featureInfo = Utils.first(featureInfos, f => f.pageIndex === pageIndex);
                  if (featureInfo.rendered) return;
                  featureInfo.rendered = true;

                  let feature = featureInfo.feature;

                  queryCommands.forEach(q => {
                    // execute the query (join or spatial)
                    switch (feature.geometryType) {
                      case "polygon": {
                        Utils.execute(q, this.mediator, {
                          layers: [
                            {
                              features: [feature]
                            }
                          ],
                          polygon: feature.geometry
                        });
                        break;
                      }

                      case "point": {
                        Utils.execute(q, this.mediator, {
                          layers: [
                            {
                              features: [feature]
                            }
                          ],
                          point: feature.geometry
                        });
                        break;
                      }

                      default: {
                        Utils.execute(q, this.mediator, {
                          feature: feature
                        });
                        break;
                      }
                    }
                  });
                });
              });
          });

          if (featureInfos.length) this.ol3popup.pages.goto(0);
        });
      });
    });
  }
}

export = Control;
